/**
 * POST /v1/feedback-board/issues/:issue_id/comments — post a customer comment.
 *
 * Admin-gated, school-scoped, rate-limited (KTD7, U4). The comment posts to the
 * linked Linear issue via `commentCreate` with customer-side attribution
 * (bot-actor `createAsUser = school label`, or a body-prefix fallback — KTD5).
 * Cross-tenant guard: the backend confirms `issue_id` belongs to the requesting
 * school's customer before posting (U4).
 *
 * The submitter's identity is server-sourced from the request context; the
 * contract carries NO author field so a client can never spoof attribution.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-18-002-feat-customer-feedback-roadmap-plan.md (U2/U4)
 */

import { z } from 'zod'
import { FeedbackCommentSchema } from '../../schemas/entities/feedback-board'

// ============================================================================
// POST /v1/feedback-board/issues/:issue_id/comments
// ============================================================================

/** Max comment body length (chars). Guards against oversized payloads (U4). */
export const MAX_FEEDBACK_COMMENT_BODY_LENGTH = 5000

/**
 * Add-comment request BODY. Carries ONLY the comment text — the target issue is
 * the URL path param (`/issues/:issue_id/comments`), the single canonical source
 * of the issue id, so it is deliberately NOT a body field (prevents a path/body
 * divergence + the guard-bypass where the body id differs from the path id). No
 * author field — identity is resolved server-side from context.
 */
export const AddFeedbackCommentRequestSchema = z.object({
  /** Comment text. */
  body: z.string().trim().min(1).max(MAX_FEEDBACK_COMMENT_BODY_LENGTH)
})
export type AddFeedbackCommentRequest = z.infer<typeof AddFeedbackCommentRequestSchema>

/** Add-comment response — the freshly created comment, projected onto the wire shape. */
export const AddFeedbackCommentResponseSchema = z.object({
  /** The created comment (already resolved to `authorKind: 'customer'`). */
  comment: FeedbackCommentSchema
})
export type AddFeedbackCommentResponse = z.infer<typeof AddFeedbackCommentResponseSchema>
